import AccessTimeIcon from "@mui/icons-material/AccessAlarm";
import CircleIcon from "@mui/icons-material/Circle";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  Tab,
  Tabs,
  Box,
  Skeleton,
  Typography,
  Stack,
  Chip,
  Grid,
  Paper,
  Container,
} from "@mui/material";
import { TabPanel } from "../../components/TabPanel";
import MessageSection from "../../components/MessageSection";
import moment from "moment";
import { useFetchData, useAppNotification } from "../../hooks/useFetchData";
import GridSkeleton from "../../components/GridSkeleton";
import NoData from "../../components/NoData";
import { useState, useEffect } from "react";
import { getUserInfoData, getAdminUsers } from "../../utils/auth";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import { useCookies } from "react-cookie";
import axiosFetch from "../../utils/axiosFetch";
import { ApiStatusTable } from "./ApiStatusTable";
import { AMAZON_CATEGORY_ORDER, AMAZON_SUBCATEGORY_ORDER } from "../../utils/constants";
import SalesforcePortal from "./SalesforcePortal";

// type = api url som/som
// pathname = /name

const ApiStatus = ({ name, api, isExternal }) => {
  const { dashboard: pDashboard } = useParams();
  const [userInfo, setUserInfo] = useState({});
  const [admin, setAdmin] = useState(false);
  const [cookies, setCookie] = useCookies(["userType"]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPsupport, setIsPSupport] = useState(true);
  const [isPsupportAmazon, setIsPSupportAmazon] = useState(true);
  const [disableNotification, setDisableNotification] = useState(false);
  const [healthData, setHealthData] = useState([]);
  const [headerName, setHeaderName] = useState([""]);
  const [categories, setCategories] = useState({});
  const infoApiUrl = `${api}/${name}`;
  const dataApiUrl = pDashboard
    ? `${api}/${name}/${pDashboard}`
    : `${api}/${name}`;
  const endPointURL = `${dataApiUrl}/realTime`;
  const redirectURL = pDashboard
    ? `/${name}/${pDashboard}/realtime`
    : `/${name}/realtime`;
  const pathHeader = name.includes("fidelity") || name.includes("amazon");
  const PathHeaderMob = name.includes("mobile");

  const { status, data } = useFetchData({
    endpoint: endPointURL,
  });
  const { notificationStatus, notification } = useAppNotification({
    endpoint: `${infoApiUrl}/getAppNotification`,
  });
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (event, newTabIndex) => {
    setTabIndex(newTabIndex);
  };

  // Define custom category order - only applied when partner is amazon and dashboard is utility
  const CATEGORY_ORDER = AMAZON_CATEGORY_ORDER;

  const SUBCATEGORY_ORDER = AMAZON_SUBCATEGORY_ORDER;

  useEffect(() => {
    if (data?.result && isExternal) {
      const uniqueCategories = [
        ...new Map(
          data?.result?.map((event) => [event.category_id, event])
        ).values(),
      ];

      const uniqueSubcategories = [
        ...new Map(
          data?.result?.map((event) => [event.subcategory_id, event])
        ).values(),
      ];

      const categoryInfo = uniqueCategories.map((category) => {
        return {
          category_name: category.category,
          category_label: category.category_label,
          subcategories: uniqueSubcategories
            .filter(
              (subcategory) => subcategory.category_id === category.category_id
            )
            .map((subcategory) => {
              return {
                subcategory_name: subcategory.subcategory_name,
                subcategory_label: subcategory.subcategory_label,
              };
            })
            .sort((a, b) => {
              if (name === 'amazon') {
                const indexA = SUBCATEGORY_ORDER.indexOf(a.subcategory_label);
                const indexB = SUBCATEGORY_ORDER.indexOf(b.subcategory_label);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
              }
              return (a.subcategory_name || '').localeCompare(b.subcategory_name || '');
            }),
        };
      });

      let sortedCategoryInfo = categoryInfo;
      if (name === 'amazon') {
        sortedCategoryInfo = categoryInfo.sort((a, b) => {
          const indexA = CATEGORY_ORDER.indexOf(a.category_name);
          const indexB = CATEGORY_ORDER.indexOf(b.category_name);
          
          // If both are in the order array, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only A is in the order array, it comes first
          if (indexA !== -1) return -1;
          // If only B is in the order array, it comes first
          if (indexB !== -1) return 1;
          // If neither is in the order array, sort alphabetically
          return a.category_name.localeCompare(b.category_name);
        });
      }

      setCategories(sortedCategoryInfo);
    }
  }, [data, isExternal, name, pDashboard]);


  useEffect(() => {
    if (name.includes("mobile")) {
      setHeaderName("Mobile App API Health Status");
    } else if (name.includes("olb")) {
      setHeaderName("OLB(Online Banking) API");
    } else {
      setHeaderName("BackEnd Services");
    }

    console.log("setting tenant", name);
    window.localStorage.setItem("tenantType", name);
  }, [name]);

  useEffect(() => {
    if (pDashboard === "prod") {
      const { token } = window.localStorage;
      const currentDay = moment().day();
      const isWeekDay = currentDay > 0 && currentDay < 6;
      const startDate =
        isWeekDay === 1
          ? moment().subtract(2, "days").format("YYYY-MM-DD")
          : moment().subtract(1, "days").format("YYYY-MM-DD");
      const endDate = moment().format("YYYY-MM-DD");
      const body = {
        startDate: `${startDate} 18:00:00`,
        endDate: `${endDate} 09:00:00`,
      };
      const postEndPointURL = `${infoApiUrl}/filehealth`;
      axiosFetch
        .post(postEndPointURL, body, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setHealthData(res.data.filter(data => !data.category.includes("CT")));
        });
    }
  }, [infoApiUrl, name, pDashboard]);

  const mobileData = PathHeaderMob
    ? data?.result?.filter((row) => row.category === "api")
    : data?.result?.filter((row) => row.category === "api");

  const isShow = false;

  const handleClick = (event) => {
    setIsPSupport(event.target.id === "pSupport");
    setIsPSupportAmazon(event.target.id === "pSupportAmazon");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const userInfoData = await getUserInfoData();
        const adminUsers = await getAdminUsers(infoApiUrl);
        if (userInfoData) {
          if (adminUsers.includes(userInfoData.userId)) {
            setAdmin(true);
          }
          setUserInfo(userInfoData);
        }
      } catch (e) {
        setUserInfo({});
      }
    };

    if (status !== "unauthorized") {
      getUserInfo();
    }
  }, [cookies.userType, infoApiUrl, status]);

  if (status === "rejected") {
    return <Navigate to="/error" />;
  }
  if (status === "unauthorized") {
    return <Navigate to="/login" />;
  }

  return (
    <Container sx={{ mt: 8 }}>
      <Stack spacing={1}>
        {userInfo.userId !== undefined && (
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <Typography variant="subtitle2">
                  {`Signed In As: ${userInfo.userId} from ${cookies.userType}`}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}

        <Grid container justifyContent="center" style={{ marginLeft: "20px" }}>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Chip
                icon={<AccessTimeIcon />}
                label={
                  <Typography variant="subtitle2">
                    {status === "loading" ? (
                      <Skeleton />
                    ) : data.lastUpdate && data.lastUpdate.length ? (
                      `Last Updated : ${moment(
                        data.lastUpdate[0].load_date
                      ).fromNow()} | Interval: ${moment(
                        data.lastUpdate[0].load_date
                      )
                        .subtract(30, "minutes")
                        .format("h:mm a")} - ${moment(
                        data.lastUpdate[0].load_date
                      ).format("h:mm a")}`
                    ) : (
                      ``
                    )}
                  </Typography>
                }
                variant="outlined"
              />

              {(pathHeader && name === 'fidelity') ? (
                <>
                  <Chip
                    label={
                      <Typography variant="subtitle2">
                        <Button
                          variant="text"
                          id="pSupport"
                          aria-describedby={id}
                          style={{ textTransform: "none" }}
                          onClick={handleClick}
                        >
                          Partner Support Contact
                        </Button>
                        |
                        <Button
                          variant="text"
                          id="mSupport"
                          aria-describedby={id}
                          style={{ textTransform: "none" }}
                          onClick={handleClick}
                        >
                          Maintenance Window
                        </Button>
                        |
                        <Button
                          style={{ textTransform: "none" }}
                          href={"/api/partner/fidelity/support"}
                          target="_blank"
                        >
                          Production Support Doc
                        </Button>
                      </Typography>
                    }
                    variant="outlined"
                  />

                  {(pDashboard === "simulation" && name === 'fidelity') && (
                    <Chip
                      label={
                        <Typography variant="subtitle2">
                          <Button
                            variant="text"
                            id="teds-dashboard"
                            aria-describedby={id}
                            // href="https://uat-item-teds-partner.us.bank-dns.com"
                            href={`${
                              process.env.NODE_ENV === "development"
                                ? "http://localhost:8085"
                                : process.env.NODE_ENV === "production"
                                ? window.location.origin
                                : window.location.origin
                            }/api/auth/teds`}
                            // target="_blank"
                            style={{ textTransform: "none", color: "white" }}
                          >
                            TEDS Dashboard
                          </Button>
                        </Typography>
                      }
                      color="secondary"
                    />
                  )}
                </>
              ) : null}

              {(pathHeader && name === 'amazon') ? (
                <>
                  <Chip
                    label={
                      <Typography variant="subtitle2">
                        <Button
                          variant="text"
                          id="pSupport"
                          aria-describedby={id}
                          style={{ textTransform: "none" }}
                          onClick={handleClick}
                        >
                          Partner Support Contact
                        </Button>
                        <Button
                          variant="text"
                          id="pSupportAmazon"
                          aria-describedby={id}
                          style={{ textTransform: "none" }}
                          onClick={handleClick}
                        >
                          Maintenance Window
                        </Button>
                        |
                        <Button
                          variant="text"
                          id="mSupportAmazon"
                          aria-describedby={id}
                          style={{ textTransform: "none" }}
                          onClick={handleClick}
                        >
                          Glossary of Status
                        </Button>
                      </Typography>
                    }
                    variant="outlined"
                  />
                </>
              ) : null}
            </Box>
          </Grid>
        </Grid>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <>
            {name === 'fidelity' && (isPsupport ? (
              <Typography sx={{ p: 4 }}>
                Partner Product Support 800-765-9549
                <ul>
                  <li>Batch Files: Option 1</li>
                  <li>Digital Platforms/APIs: Option 2</li>
                </ul>
              </Typography>
            ) : (
              <Typography sx={{ p: 6 }}>
                Standard Maintenance Windows
                <ul>
                  <li>Elan: Every Sunday 12:00-7:00 AM CST</li>
                  <li>Epsilon Rewards: Every Sunday 12:00-8am EST</li>
                  <li>Fidelity: Every Sunday 3:00-5:00am EST</li>
                </ul>
              </Typography>
            ))}

            {name === 'amazon' && (isPsupport ? (
              <Typography sx={{ p: 4 }}>
                Partner Product Support 1-800-790-5784
              </Typography>
            ) : isPsupportAmazon ? (
              <Typography sx={{ p: 6 }}>
                Standard Maintenance Window (Production Environment)
                <ul>
                  <li>U.S. Bank: Every Sunday 12:00-7:00AM CST</li>
                  <li>Epsilon Rewards: Every Sunday 12:00-8:00AM CST</li>
                </ul>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  *For detailed maintenance window information, please refer to the{' '}
                  <span style={{ color: 'lightgreen' }}>
                    Amazon &amp; U.S. Bank Technology Management SOP.
                  </span>
                </Typography>
              </Typography>
            ) : (
              <Typography sx={{ p: 4 }}>
                Glossary of Status
                <ul>
                  <li><Typography sx={{display: 'inline'}} color="green">Green</Typography>: 95+ Success</li>
                  <li><Typography sx={{display: 'inline'}} color="#bfa100">Yellow</Typography>: 95-90% Success</li>
                  <li><Typography sx={{display: 'inline'}} color="red">Red</Typography>: {'<90% Success'}
                  </li>
                  <li><Typography sx={{display: 'inline'}} color="gray">Gray</Typography>: {'No Activity (within specified window)'}
                  </li>
                </ul>
              </Typography>
            ))}
          </>
        </Popover>

        {name === 'amazon' && <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <SalesforcePortal/>
          </Grid>
        </Grid>}

        {notificationStatus === "resolved" &&
        (admin || (notification && !disableNotification)) ? (
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <h3>Notification</h3>
              <MessageSection
                type={infoApiUrl}
                admin={admin}
                userId={userInfo.userId}
                initialNotification={notification}
                handleNotification={(val) => setDisableNotification(val)}
              />
            </Grid>
          </Grid>
        ) : null}
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            {status === "loading" ? (
              <GridSkeleton rows={5} cols={8} />
            ) : data?.result === undefined || data?.result?.length === 0 ? (
              <NoData />
            ) : isExternal ? (
              <div>
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={tabIndex}
                      onChange={handleTabChange}
                      variant="scrollable"
                      aria-label="api status tabs"
                    >
                      {categories?.map((category, index) => {
                        return (
                          <Tab
                            label={category.category_label}
                            id={`simple-tab-${index}`}
                            aria-controls={`simple-tabpanel-${index}`}
                          />
                        );
                      })}

                      {pDashboard === "prod" && name==='fidelity'&& (
                        <Tab
                          label="File Health Status"
                          id={`simple-tab-${categories?.length}`}
                          aria-controls={`simple-tabpanel-${categories?.length}`}
                        />
                      )}
                    </Tabs>
                  </Box>

                  {categories?.map((category, index) => {
                    return (
                      <TabPanel value={tabIndex} index={index}>
                        {category?.subcategories
                          ?.slice()
                          ?.sort((a, b) => {
                            if (a.subcategory_name === "default" && b.subcategory_name !== "default") return 1;
                            if (a.subcategory_name !== "default" && b.subcategory_name === "default") return -1;
                            return 0;
                          })
                          ?.map((subcategory, index) => {
                            return (
                              <>
                                <h3 style={{ textAlign: "center" }}>
                                  {subcategory.subcategory_label}
                                </h3>
                                <ApiStatusTable
                                  data={data?.result?.filter(
                                    (row) =>
                                      row.category === category.category_name &&
                                      row.subcategory_name ===
                                        subcategory.subcategory_name
                                  )}
                                  sortKey="event_label"
                                  redirectUrl={redirectURL}
                                />
                              </>
                            );
                          })}
                      </TabPanel>
                    );
                  })}

                  {pDashboard === "prod" && (
                    <TabPanel value={tabIndex} index={categories?.length}>
                      <h3 style={{ textAlign: "center" }}>{""}</h3>
                      <ApiStatusTable
                        data={healthData}
                        sortKey="category"
                        redirectUrl={redirectURL}
                      />
                    </TabPanel>
                  )}
                </Box>
              </div>
            ) : (
              <div>
                <h3 style={{ textAlign: "center" }}>{headerName}</h3>
                <ApiStatusTable
                  data={mobileData}
                  sortKey="event_label"
                  redirectUrl={redirectURL}
                />
              </div>
            )}
          </Grid>
        </Grid>

        {status === "resolved" && isShow ? (
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ paddingY: 1 }}>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <CircleIcon color="success" />
                  <Typography variant="subtitle1" gutterBottom component="div">
                    &gt; 99.99%
                  </Typography>
                  <CircleIcon color="warning" />
                  <Typography variant="subtitle1" gutterBottom component="div">
                    90% to 99.98%
                  </Typography>
                  <CircleIcon color="error" />
                  <Typography variant="subtitle1" gutterBottom component="div">
                    &lt; 90%
                  </Typography>
                  <CircleIcon color="disabled" />
                  <Typography variant="subtitle1" gutterBottom component="div">
                    No Activity
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        ) : null}
      </Stack>
    </Container>
  );
};

export default ApiStatus;
